import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CommonDialogComponent } from '@components/system-setting/common-dialog/common-dialog.component';
import { SystemSettingTab, CommonLanguageLabel, CommonMasterClockLabel } from '@models/system-setting';
import { AuthService } from '@service/auth.service';
import { CommonService } from '@service/common.service';
import { CommonSocketService } from '@service/common-socket.service';
import { Common, commonLanguages, MasterClockType } from 'icreate/common/models/common';
import { Vessel } from 'icreate/common/models/vessel';
import { Subject, takeUntil } from 'rxjs';
import { MessageDialogService } from '@service/message-dialog.service';
import { ActivatedRoute, Router } from '@angular/router';
import { VesselSocketService } from '@service/vessel-socket.service';
import { SystemSettingEapComponent } from '@components/system-setting/system-setting-eap/system-setting-eap.component';
import { VesselService } from '@service/vessel.service';

@Component({
    selector: 'app-system-setting',
    templateUrl: './system-setting.component.html',
    styleUrls: ['./system-setting.component.css'],
    standalone: false,
})
export class SystemSettingComponent implements OnInit, OnDestroy {
    readonly TABS = Object.values(SystemSettingTab);

    readonly OPS = SystemSettingTab.ops;
    readonly FCU = SystemSettingTab.fcu;
    readonly EAP = SystemSettingTab.eap;

    readonly ENGLISH = commonLanguages.EN;
    readonly JAPANESE = commonLanguages.JP;

    @Input() vessel!: Vessel;

    @ViewChild(SystemSettingEapComponent) eapComponent!: SystemSettingEapComponent;

    currentTab: SystemSettingTab = null;
    common: Common;
    languageLabel: CommonLanguageLabel;
    masterClockLabel: string;
    utcLabel: any;
    isJrcsEmployee: boolean = false;
    canEditSettings: boolean = true;
    isLoading: boolean = false;

    private destroySubscriptionSubject$ = new Subject<void>();

    constructor(
        private dialog: MatDialog,
        private commonService: CommonService,
        private vesselService: VesselService,
        private commonSocketService: CommonSocketService,
        private vesselSocketService: VesselSocketService,
        private authService: AuthService,
        private messageDialogService: MessageDialogService,
        private route: ActivatedRoute,
        private router: Router
    ) {}

    async ngOnInit() {
        this.isLoading = true;
        const tab = this.route.snapshot.queryParams.tab;

        if (tab === 'fcu') {
            this.currentTab = this.FCU;
            this.router.navigate([], {
                queryParams: { tab: null },
                queryParamsHandling: 'merge',
            });
        }

        this.vesselService.accessUpdated$.pipe(takeUntil(this.destroySubscriptionSubject$)).subscribe(canEdit => {
            this.canEditSettings = canEdit;
        });

        const currentUser = this.authService.getCurrentUser();
        this.isJrcsEmployee = await this.commonService.isJrcsEmployee(currentUser.email);

        const common = await this.commonService.getOne(this.vessel.id);
        if (common) {
            this.common = common;
        } else {
            this.common = {
                id: '',
                vesselId: this.vessel.id,
                language: commonLanguages.EN,
                masterClockType: MasterClockType.MasterPulse,
                UtcEnable: 0,
                japanCoastGuard: 0,
            };
        }
        this.setLabel(this.common);
        this.isLoading = false;

        this.commonSocketService
            .commonCreated$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(newCommon => {
                if (newCommon.vesselId === this.vessel.id) {
                    this.updateHandler(newCommon);
                }
            });

        this.commonSocketService
            .commonUpdated$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(updatedCommon => {
                if (updatedCommon.vesselId === this.vessel.id) {
                    this.updateHandler(updatedCommon);
                }
            });

        this.vesselSocketService
            .vesselUpdated$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(updatedVessel => {
                if (updatedVessel.id === this.vessel.id) {
                    this.vessel = updatedVessel;
                }
            });
    }

    ngOnDestroy(): void {
        this.destroySubscriptionSubject$.next();
        this.destroySubscriptionSubject$.complete();
    }

    updateHandler(data: Common) {
        if (data.vesselId === this.vessel.id) {
            this.common = data;
            this.setLabel(this.common);
            this.showMessageDialog('system-settings.general.save.message');
        }
    }

    showMessageDialog(message) {
        this.messageDialogService.openMessage(message);
    }

    openGeneralDialog() {
        this.dialog.open(CommonDialogComponent, {
            data: {
                id: this.common?.id || '',
                vesselId: this.vessel.id,
                drawingId: this.vessel.drawingId,
                language: this.common?.language || commonLanguages.JP,
                masterClockType: this.common?.masterClockType ?? MasterClockType.MasterPulse,
                UtcEnable: this.common?.UtcEnable ?? 1,
                japanCoastGuard: this.common?.japanCoastGuard ?? 0,
                isJrcsEmployee: this.isJrcsEmployee,
            },
            disableClose: true,
            autoFocus: false,
        });
    }

    setLabel(common: Common) {
        const languageMap = { EN: CommonLanguageLabel.EN, JP: CommonLanguageLabel.JP };
        this.languageLabel = languageMap[common.language];
        this.masterClockLabel = CommonMasterClockLabel[common.masterClockType];
        this.utcLabel = common.UtcEnable ? 'Enable' : 'Disable';
    }

    async selectTab(tab: SystemSettingTab) {
        const canLeave = await this.canExit();
        if (!canLeave) return;

        if (tab) {
            this.currentTab = tab;
        } else {
            const getUrlTab = this.route.snapshot.paramMap.get('tab');
            if (getUrlTab) await this.router.navigate([`home/${this.vessel.id}/settings`]);
            else this.currentTab = tab;
        }
    }

    saveTab(tab: SystemSettingTab) {
        if (tab === SystemSettingTab.eap) {
            this.eapComponent.save();
        }
    }

    saveDisabled(tab: SystemSettingTab): boolean {
        if (tab === SystemSettingTab.eap && this.eapComponent) {
            return this.eapComponent.getSaveButtonDisabled();
        }
        return false;
    }

    async canExit(): Promise<boolean> {
        if (this.eapComponent && !this.eapComponent.getSaveButtonDisabled()) {
            return await this.eapComponent.exitWithoutSave();
        }

        return true;
    }
}
